import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Dialog, HStack, Portal, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import { useAgentTypes } from "@/components/layout/AgentTypesContext";
import { ModelService } from "@/services/ModelService";

const PickerRow = ({ label, onClick }) => (
    <HStack
        w="full"
        justify="space-between"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="gray.100"
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={onClick}
    >
        <Text color="gray.900" fontSize="sm" fontWeight={500}>{label}</Text>
        <HiOutlineChevronRight size={14} color="#9A96A6" />
    </HStack>
);

const NewRecruitmentModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation("home");
    const navigate = useNavigate();
    const { recruiterAgents } = useAgentTypes();

    const [selectedAgentId, setSelectedAgentId] = useState(null);
    const [agentData, setAgentData] = useState(null);
    const [isLoadingScenarios, setIsLoadingScenarios] = useState(false);
    // True while pre-loading the target scenario page's own data, right before navigating --
    // keeps the spinner inside this popup instead of the user landing on a blank/loading
    // ScenarioDetailPage with no idea what's happening.
    const [isPreparingForm, setIsPreparingForm] = useState(false);

    // Reset to a clean first step every time the popup opens, auto-picking the only
    // agent when there's just one so that case skips straight to its scenarios.
    useEffect(() => {
        if (!isOpen) return;
        setAgentData(null);
        setIsPreparingForm(false);
        setSelectedAgentId(recruiterAgents.length === 1 ? recruiterAgents[0].id : null);
    }, [isOpen, recruiterAgents]);

    useEffect(() => {
        if (!isOpen || !selectedAgentId) return;
        let cancelled = false;
        setIsLoadingScenarios(true);
        setAgentData(null);

        ModelService.getById(selectedAgentId).then((data) => {
            if (cancelled) return;
            setAgentData(data);
            const list = data?.scenarios ?? [];
            // Only one scenario for this agent -- skip the second step entirely.
            if (list.length === 1) {
                goToScenario(data, list[0]);
            }
        }).finally(() => {
            if (!cancelled) setIsLoadingScenarios(false);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selectedAgentId]);

    // Pre-fetches exactly what ScenarioDetailPage needs so it can render the create form
    // immediately on arrival instead of showing its own loading spinner first.
    const goToScenario = async (data, scenario) => {
        setIsPreparingForm(true);
        const recruitmentsData = await ModelService.getRecruitments(scenario.id).catch(() => null);
        onClose();
        navigate(`/models/${data.id}/scenarios/${scenario.id}`, {
            state: {
                autoOpenCreate: true,
                prefetchedAgent: data,
                prefetchedRecruitments: recruitmentsData?.items ?? [],
            },
        });
    };

    const scenarios = agentData?.scenarios ?? null;
    const showScenarioStep = !!selectedAgentId;
    const showLoading = selectedAgentId && (isLoadingScenarios || isPreparingForm || scenarios?.length === 1);
    const showNoScenarios = selectedAgentId && scenarios && scenarios.length === 0;
    const canGoBack = showScenarioStep && recruiterAgents.length > 1 && !isPreparingForm;

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => { if (!e.open) onClose(); }} placement="center">
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content bg="white" border="1px solid" borderColor="gray.200" maxW="440px">
                        <Dialog.Header>
                            <HStack w="full" justify="space-between" align="center">
                                <HStack gap={2} align="center" minW={0}>
                                    {canGoBack && (
                                        <Box
                                            as="button" type="button"
                                            onClick={() => { setSelectedAgentId(null); setAgentData(null); }}
                                            display="flex" color="gray.500" _hover={{ color: "gray.700" }} flexShrink={0}
                                        >
                                            <HiOutlineChevronLeft />
                                        </Box>
                                    )}
                                    <Dialog.Title color="gray.900" fontSize="md" truncate>
                                        {isPreparingForm
                                            ? t("newRecruitmentPicker.preparingForm")
                                            : showScenarioStep ? t("newRecruitmentPicker.chooseScenarioTitle") : t("newRecruitmentPicker.chooseAgentTitle")}
                                    </Dialog.Title>
                                </HStack>
                                <Box as="button" type="button" onClick={onClose} color="gray.400" fontSize="lg" lineHeight={1} _hover={{ color: "gray.600" }}>
                                    ×
                                </Box>
                            </HStack>
                        </Dialog.Header>
                        <Dialog.Body pb={6}>
                            {showLoading ? (
                                <VStack py={8} align="center">
                                    <Spinner color="purple.500" />
                                </VStack>
                            ) : showNoScenarios ? (
                                <VStack align="center" gap={3} py={4}>
                                    <Text color="gray.500" fontSize="sm" textAlign="center">{t("newRecruitmentPicker.noScenarios")}</Text>
                                </VStack>
                            ) : (
                                <VStack align="stretch" gap={0} border="1px solid" borderColor="gray.100" borderRadius={10} overflow="hidden">
                                    {!showScenarioStep && recruiterAgents.map((agent) => (
                                        <PickerRow key={agent.id} label={agent.name} onClick={() => setSelectedAgentId(agent.id)} />
                                    ))}
                                    {showScenarioStep && scenarios?.map((scenario) => (
                                        <PickerRow key={scenario.id} label={scenario.name} onClick={() => goToScenario(agentData, scenario)} />
                                    ))}
                                </VStack>
                            )}
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default NewRecruitmentModal;
